import React, { useEffect, useRef, useState } from 'react';
import './css/Gallery.css';

// ── Your actual art imports ───────────────────────────────────────

import art1 from './assets/IMG_6394.jpeg';         // angel sketch
import art2 from './assets/IMG_6393.jpeg';         // eye painting
import art3 from './assets/IMG_20200723_160936.jpg'; // face ballpoint on fabric
import art4 from './assets/IMG_20220714_125537.jpg'; // Shiva sketch
import art5 from './assets/1000050727-01.jpeg';    // always — HP blue
import art6 from './assets/IMG_20231111_222228228.jpg'; // 3 divine paintings on wall
import art7 from './assets/illustration2.jpeg';    // aladdin lamp digital
import art8 from './assets/illustration1.jpg';     // angel in purple digital
import art9 from './assets/illustration3.jpg';     // girl + dog starry night digital

// ── Tile configs: vary width so the wall feels organic ───────────
const allTiles = [
  { src: art1, w: 220, label: 'Fallen Angel' },
  { src: art2, w: 300, label: 'The Eye' },
  { src: art3, w: 180, label: 'Ballpoint Study' },
  { src: art4, w: 240, label: 'Mahadev' },
  { src: art5, w: 200, label: 'Always' },
  { src: art6, w: 380, label: 'Divine Trinity' },
  { src: art7, w: 220, label: 'Aladdin\'s Lamp' },
  { src: art8, w: 200, label: 'Guardian Angel' },
  { src: art9, w: 260, label: 'Starry Night' },
];

// Distribute into 3 rows then triple-duplicate for infinite scroll
function buildRows(tiles) {
  const r1 = [tiles[0], tiles[3], tiles[6], tiles[1], tiles[7]];
  const r2 = [tiles[4], tiles[1], tiles[8], tiles[2], tiles[5]];
  const r3 = [tiles[7], tiles[5], tiles[3], tiles[0], tiles[8], tiles[6]];
  return [
    [...r1, ...r1, ...r1],
    [...r2, ...r2, ...r2],
    [...r3, ...r3, ...r3],
  ];
}

const ROWS = buildRows(allTiles);

const ROW_CONFIG = [
  { speed: '55s', dir: 'normal', h: '230px' },
  { speed: '75s', dir: 'reverse', h: '270px' },
  { speed: '45s', dir: 'normal', h: '210px' },
];

const Gallery = () => {
  const [entered, setEntered] = useState(false);
  const [hoveredTile, setHoveredTile] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const sectionRef = useRef(null);
  const quoteRef = useRef(null);
  const overlayRef = useRef(null);

  // Scroll-triggered entrance
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setEntered(true); },
      { threshold: 0.25 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  // Mouse parallax on quote
  useEffect(() => {
    const move = (e) => {
      if (!quoteRef.current) return;
      const dx = (e.clientX / window.innerWidth - 0.5) * 18;
      const dy = (e.clientY / window.innerHeight - 0.5) * 10;
      quoteRef.current.style.transform =
        `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
    };
    window.addEventListener('mousemove', move);
    return () => window.removeEventListener('mousemove', move);
  }, []);

  // Close lightbox on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      {/* ── Lightbox ── */}

      {/* ── Lightbox ── */}
      {lightbox && (
        <div
          className="gallery-lightbox"
          onClick={() => setLightbox(null)}
        >
          <button
            className="gallery-lightbox-close"
            onClick={() => setLightbox(null)}
          >✕</button>
          <img src={lightbox.src} alt={lightbox.label} />
          <span className="gallery-lightbox-label">{lightbox.label}</span>
        </div>
      )}

      {/* ── Gallery ── */}
      <div
        ref={sectionRef}
        className={`gallery-section ${entered ? 'entered' : ''}`}
      >

        {/* Drifting rows */}
        {ROWS.map((row, ri) => (
          <div key={ri} className="gallery-row-wrap">
            <div
              className={`gallery-track gallery-track-${ri}`}
              style={{ '--spd': ROW_CONFIG[ri].speed }}
            >
              {row.map((tile, ti) => (
                <div
                  key={ti}
                  className="gallery-tile"
                  style={{
                    width: tile.w + 'px',
                    height: ROW_CONFIG[ri].h,
                  }}
                  onClick={() => setLightbox(tile)}
                  onMouseEnter={() => setHoveredTile(`${ri}-${ti}`)}
                  onMouseLeave={() => setHoveredTile(null)}
                >
                  <img
                    src={tile.src}
                    alt={tile.label}
                    loading="lazy"
                    draggable={false}
                  />
                  <span className="gallery-tile-label">{tile.label}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Cinematic vignette + fade edges */}
        <div className="gallery-overlay" />

        {/* Floating quote */}
        <div className="gallery-quote" ref={quoteRef}>
          <span className="gallery-quote-line1">
            What is this earth without art?
          </span>
          <div className="gallery-quote-divider" />
          <span className="gallery-quote-line2">Just &nbsp; a &nbsp; rock</span>
        </div>

      </div>
    </>
  );
};

export default Gallery;